/**
 * Coordinate projection module — GPS <-> canvas pixel conversions.
 *
 * All functions are pure (no side effects) and suitable for unit testing.
 *
 * Coordinate system:
 *   - Bounding box is defined by { topLat, leftLng, bottomLat, rightLng }
 *   - topLat > bottomLat (north is higher latitude)
 *   - Canvas pixel (0, 0) = top-left = (topLat, leftLng)
 *   - Y-axis is FLIPPED: higher latitude = lower y pixel value
 *
 * Projection model: equirectangular (linear mapping) — sufficient accuracy
 * for small land parcels where the curvature of the earth is negligible.
 */

/**
 * Convert GPS coordinates to canvas pixel coordinates.
 *
 * @param {number} lat - Latitude in decimal degrees
 * @param {number} lng - Longitude in decimal degrees
 * @param {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number }} bbox
 * @param {{ width: number, height: number }} canvasSize
 * @returns {{ x: number, y: number }}
 */
export function latLngToPixel(lat, lng, bbox, canvasSize) {
  const x = ((lng - bbox.leftLng) / (bbox.rightLng - bbox.leftLng)) * canvasSize.width;
  // Y-axis flip: higher lat maps to lower y (top of canvas)
  const y = ((bbox.topLat - lat) / (bbox.topLat - bbox.bottomLat)) * canvasSize.height;
  return { x, y };
}

/**
 * Convert canvas pixel coordinates back to GPS coordinates.
 *
 * @param {number} x - Pixel x coordinate
 * @param {number} y - Pixel y coordinate
 * @param {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number }} bbox
 * @param {{ width: number, height: number }} canvasSize
 * @returns {{ lat: number, lng: number }}
 */
export function pixelToLatLng(x, y, bbox, canvasSize) {
  const lng = bbox.leftLng + (x / canvasSize.width) * (bbox.rightLng - bbox.leftLng);
  // Y-axis flip: y=0 maps to topLat
  const lat = bbox.topLat - (y / canvasSize.height) * (bbox.topLat - bbox.bottomLat);
  return { lat, lng };
}

/**
 * Validate a bounding box definition.
 *
 * @param {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number }} bbox
 * @returns {string[]} Array of error messages (empty = valid)
 */
export function validateBbox({ topLat, leftLng, bottomLat, rightLng }) {
  const errors = [];

  if (typeof topLat !== "number" || topLat < -90 || topLat > 90) {
    errors.push("topLat must be between -90 and 90");
  }
  if (typeof bottomLat !== "number" || bottomLat < -90 || bottomLat > 90) {
    errors.push("bottomLat must be between -90 and 90");
  }
  if (typeof leftLng !== "number" || leftLng < -180 || leftLng > 180) {
    errors.push("leftLng must be between -180 and 180");
  }
  if (typeof rightLng !== "number" || rightLng < -180 || rightLng > 180) {
    errors.push("rightLng must be between -180 and 180");
  }

  // Check ordering only if individual values are in range
  if (errors.length === 0) {
    if (topLat <= bottomLat) {
      errors.push("topLat must be greater than bottomLat (north must be above south)");
    }
    if (leftLng >= rightLng) {
      errors.push("leftLng must be less than rightLng (west must be left of east)");
    }
  }

  return errors;
}

/**
 * Calculate the approximate area of a bounding box in square kilometres.
 *
 * Uses the Haversine formula to compute the width and height of the bounding
 * box at its centre latitude, then multiplies them together. This gives a
 * reasonable approximation for small land parcels.
 *
 * @param {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number }} bbox
 * @returns {number} Area in km²
 */
export function bboxAreaKm2({ topLat, leftLng, bottomLat, rightLng }) {
  const R = 6371; // Earth's mean radius in km

  // Height: north-south distance along the left edge (constant longitude)
  const lat1Rad = toRad(bottomLat);
  const lat2Rad = toRad(topLat);
  const dLat = lat2Rad - lat1Rad;
  const aHeight = Math.sin(dLat / 2) ** 2;
  const height = 2 * R * Math.asin(Math.sqrt(aHeight));

  // Width: east-west distance along the centre latitude
  const midLat = toRad((topLat + bottomLat) / 2);
  const dLng = toRad(rightLng - leftLng);
  const aWidth = Math.cos(midLat) ** 2 * Math.sin(dLng / 2) ** 2;
  const width = 2 * R * Math.asin(Math.sqrt(aWidth));

  return width * height;
}

/**
 * Calculate the approximate area of a polygon in square metres.
 *
 * Uses an equirectangular projection: converts lat/lng vertices to local
 * metre coordinates using the centroid latitude, then applies the Shoelace
 * formula for polygon area. Sufficient accuracy for small land parcels.
 *
 * @param {Array<{lat: number, lng: number}>} vertices
 * @returns {number} Area in m² (always non-negative)
 */
export function polygonAreaM2(vertices) {
  if (vertices.length < 3) return 0;

  // Centroid latitude for equirectangular scaling
  const centLat = vertices.reduce((sum, v) => sum + v.lat, 0) / vertices.length;

  const metersPerDegreeLat = 111320;
  const metersPerDegreeLng = 111320 * Math.cos((centLat * Math.PI) / 180);

  // Convert to local metre coordinates
  const pts = vertices.map((v) => ({
    x: v.lng * metersPerDegreeLng,
    y: v.lat * metersPerDegreeLat,
  }));

  // Shoelace formula
  let area = 0;
  const n = pts.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    area += pts[i].x * pts[j].y;
    area -= pts[j].x * pts[i].y;
  }

  return Math.abs(area / 2);
}

/**
 * Calculate the approximate distance between two polygon vertices in metres.
 *
 * Uses an equirectangular projection: metres-per-degree are computed at the
 * midpoint latitude between the two vertices, then Pythagoras gives the
 * straight-line distance. Sufficient accuracy for small land parcels.
 *
 * @param {{ lat: number, lng: number }} v1
 * @param {{ lat: number, lng: number }} v2
 * @returns {number} Distance in metres (always non-negative)
 */
export function edgeLengthM(v1, v2) {
  const midLat = (v1.lat + v2.lat) / 2;
  const metersPerDegreeLat = 111320;
  const metersPerDegreeLng = 111320 * Math.cos((midLat * Math.PI) / 180);

  const dx = (v2.lng - v1.lng) * metersPerDegreeLng;
  const dy = (v2.lat - v1.lat) * metersPerDegreeLat;

  return Math.hypot(dx, dy);
}

/** Convert degrees to radians. */
function toRad(degrees) {
  return (degrees * Math.PI) / 180;
}
